import React, { useState, useRef } from 'react';

interface Step1CVUploadProps {
  file: File | null;
  name: string;
  linkedinUrl: string;
  onUpdate: (file: File | null, name: string, linkedinUrl: string) => void;
  onNext: () => void;
}

export const Step1CVUpload: React.FC<Step1CVUploadProps> = ({
  file,
  name,
  linkedinUrl,
  onUpdate,
  onNext,
}) => {
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [localName, setLocalName] = useState(name);
  const [localLinkedinUrl, setLocalLinkedinUrl] = useState(linkedinUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supported file types
  const SUPPORTED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/msword', // .doc
    'text/plain', // .txt
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_TYPES.includes(file.type)) {
      return 'Please upload a PDF, DOCX, DOC, or TXT file';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB';
    }
    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      onUpdate(null, localName, localLinkedinUrl);
      return;
    }
    setError('');
    onUpdate(selectedFile, localName, localLinkedinUrl);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setLocalName(newName);
    onUpdate(file, newName, localLinkedinUrl);
  };

  const handleLinkedinUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setLocalLinkedinUrl(newUrl);
    onUpdate(file, localName, newUrl);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    onUpdate(null, localName, localLinkedinUrl);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNext = () => {
    if (!file) {
      setError('Please upload your CV to continue');
      return;
    }
    if (!localName.trim()) {
      setError('Please enter your name to continue');
      return;
    }
    if (!localLinkedinUrl.trim()) {
      setError('Please enter your LinkedIn URL to continue');
      return;
    }
    setError('');
    onNext();
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toUpperCase() || '';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">Let's get started</h1>
      <p className="text-xs md:text-base text-gray-600 mb-4 md:mb-8">Tell us about yourself and upload your CV</p>

      <div className="space-y-4 md:space-y-6">
        {/* Name Input Field */}
        <div>
          <label htmlFor="name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Your Full Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={localName}
            onChange={handleNameChange}
            placeholder="e.g., John Doe"
            className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
          />
        </div>

        {/* LinkedIn URL Input Field */}
        <div>
          <label htmlFor="linkedin" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            <div className="flex items-center gap-1.5 md:gap-2">
              <svg className="w-4 h-4 md:w-5 md:h-5 text-[#0077b5]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span>LinkedIn Profile URL <span className="text-red-500">*</span></span>
            </div>
          </label>
          <input
            id="linkedin"
            type="url"
            value={localLinkedinUrl}
            onChange={handleLinkedinUrlChange}
            placeholder="e.g., https://www.linkedin.com/in/your-profile"
            className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
          />
        </div>

        {/* File Upload Area */}
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-4 md:p-8 text-center cursor-pointer transition-all ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : file
              ? 'border-green-300 bg-green-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {!file ? (
            <>
              {/* Upload Icon */}
              <svg
                className={`mx-auto h-8 w-8 md:h-12 md:w-12 mb-2 md:mb-4 ${
                  isDragging ? 'text-blue-500' : 'text-gray-400'
                }`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <p className="text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                {isDragging ? 'Drop your file here' : 'Click to upload CV or drag and drop'}
              </p>
              <p className="text-xs md:text-sm text-gray-500">
                PDF, DOCX, DOC, or TXT (max 10MB)
              </p>
            </>
          ) : (
            <>
              {/* File Preview */}
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-medium text-gray-900 truncate text-sm md:text-base">{file.name}</p>
                  <p className="text-xs md:text-sm text-gray-500">
                    {getFileExtension(file.name)} • {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  className="flex-shrink-0 p-1.5 md:p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  title="Remove file"
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>

        {error && <p className="text-xs md:text-sm text-red-600 text-center">{error}</p>}

        <button
          onClick={handleNext}
          className="w-full bg-blue-600 text-white py-2.5 md:py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

